import { linkOptions } from '@tanstack/react-router';

import { Text } from '../cdl/Text/Text';
import { translate } from '../common/helpers/translate.helper';
import { useDocumentTitle } from '../common/hooks/useDocumentTitle';
import { useRole } from '../common/hooks/useRole';
import { IconBuildingSkyscraper } from '../common/icons/cdl/BuildingSkyscraper';
import { IconTerminal2 } from '../common/icons/cdl/Terminal2';
import { IconUser } from '../common/icons/cdl/User';
import { IconUsers } from '../common/icons/cdl/Users';
import { MenuItem } from '../common/MenuItem/MenuItem';
import { Page } from '../common/Page/Page';
import { Box } from '../common/ui/Box';
import { CustomerGroupTabs } from '../group/customer/CustomerGroupTabs';
import { SupplierGroupTabs } from '../group/supplier/SupplierGroupTabs';

import { SuperAdminGroupSettingsSection } from './SuperAdminGroupSettingsSection';

export const SettingsPage = () => {
    const role = useRole();

    useDocumentTitle(translate('settings.title'));

    return (
        <Page>
            <Box display="flex" flexDirection="column" gap={40}>
                <Box display="flex" flexDirection="column" gap={16}>
                    <Text variant="subtitle1">{translate('settings.personalSettings.title')}</Text>
                    <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(348px, 348px))" gap={48}>
                        <MenuItem
                            title={translate('settings.personalDetails.title')}
                            description={translate('settings.personalDetails.description')}
                            Icon={IconUser}
                            linkOptions={linkOptions({ to: '/me' })}
                        />
                    </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={16}>
                    <Text variant="subtitle1">{translate('settings.accountSettings.title')}</Text>
                    <Box
                        display="grid"
                        gridTemplateColumns="repeat(auto-fit, minmax(348px, 348px))"
                        columnGap={48}
                        rowGap={16}
                    >
                        {!role.isSuperAdmin() && role.hasAdminRights() ? (
                            <MenuItem
                                title={translate('settings.groupManagement.title')}
                                description={translate('settings.groupManagement.description')}
                                Icon={IconBuildingSkyscraper}
                                linkOptions={linkOptions({
                                    to: role.isCustomer() ? '/customer-groups/$groupId' : '/supplier-groups/$groupId',
                                    params: { groupId: role.group.id },
                                })}
                            />
                        ) : null}

                        {!role.isSuperAdmin() && !role.hasAdminRights() && role.hasAnyAdminRights() ? (
                            <MenuItem
                                title={translate('settings.companyManagement.title')}
                                description={translate('settings.companyManagement.description')}
                                Icon={IconBuildingSkyscraper}
                                linkOptions={linkOptions({
                                    to: role.isCustomer()
                                        ? '/customer-groups/$groupId/companies'
                                        : '/supplier-groups/$groupId/companies',
                                    params: { groupId: role.group.id },
                                })}
                            />
                        ) : null}

                        <MenuItem
                            title={translate('settings.members.title')}
                            description={translate('settings.members.description')}
                            Icon={IconUsers}
                            linkOptions={linkOptions({
                                to: role.isSuperAdmin()
                                    ? '/members'
                                    : role.isCustomer()
                                      ? '/customer-groups/$groupId/members'
                                      : '/supplier-groups/$groupId/members',
                                params: !role.isSuperAdmin() ? { groupId: role.group.id } : undefined,
                            })}
                        />

                        {!role.isSuperAdmin() && role.hasAdminRights() && role.isCustomer() ? (
                            <MenuItem
                                title={translate('settings.developers.title')}
                                description={translate('settings.developers.customers.description')}
                                Icon={IconTerminal2}
                                linkOptions={linkOptions({
                                    to: '/customer-groups/$groupId',
                                    params: { groupId: role.group.id },
                                    search: {
                                        tab: CustomerGroupTabs.DEVELOPERS,
                                    },
                                })}
                            />
                        ) : null}

                        {!role.isSuperAdmin() && role.hasAdminRights() && role.isSupplier() ? (
                            <MenuItem
                                title={translate('settings.developers.title')}
                                description={translate('settings.developers.suppliers.description')}
                                Icon={IconTerminal2}
                                linkOptions={linkOptions({
                                    to: '/supplier-groups/$groupId',
                                    params: { groupId: role.group.id },
                                    search: {
                                        tab: SupplierGroupTabs.DEVELOPERS,
                                    },
                                })}
                            />
                        ) : null}
                    </Box>
                </Box>

                <SuperAdminGroupSettingsSection />
            </Box>
        </Page>
    );
};
